import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSmoothScroll } from '../hooks/useSmoothScroll';

const SectionLink = ({ 
  href, 
  children, 
  className = "",
  onClick = null,
  variant = "default"
}) => {
  const [isActive, setIsActive] = useState(false);
  const targetRef = useRef(null);
  const { scrollToSection } = useSmoothScroll();
  
  const baseClasses = "text-white/80 hover:text-white transition-colors duration-300 font-medium relative group";
  
  const variants = {
    default: "",
    nav: `text-white/80 hover:text-white transition-colors duration-300 font-medium relative group ${
      isActive ? 'text-white' : ''
    }`
  };

  const linkClasses = `${variants[variant]} ${className}`;

  // Track active section
  useEffect(() => {
    const targetId = href.replace('#', '');
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsActive(true);
          } else {
            setIsActive(false);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-80px 0px -50% 0px'
      }
    );

    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => {
      if (targetElement) {
        observer.unobserve(targetElement);
      }
    };
  }, [href]);

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
    
    // Handle external links
    if (href.startsWith('http')) {
      return;
    }
    
    e.preventDefault();
    
    // If not on home page, navigate to home with hash
    if (window.location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    
    // Use the smooth scroll hook
    const targetId = href.replace('#', '');
    scrollToSection(targetId, 20);
  };

  return (
    <motion.a
      ref={targetRef}
      href={href}
      onClick={handleClick}
      className={linkClasses}
      whileHover={{ y: -1 }}
      transition={{ duration: 0.2 }}
    >
      {children}
      {variant === "nav" && (
        <motion.span 
          className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-400"
          initial={{ width: 0 }}
          whileHover={{ width: "100%" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        />
      )}
    </motion.a>
  );
};

export default SectionLink; 